import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Logo from "../../../components/Logo";
import ProgressSteps from "../../../components/ProgressSteps";
import { signup } from "../../../api/authEtablissement";

const SIGNUP_STORAGE_KEY = "maps.signup";
const SIGNUP_CREDENTIALS_KEY = "agbaza.signup.credentials";

export default function EtablissementSignupDocuments() {
  const navigate = useNavigate();
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const raw = localStorage.getItem(SIGNUP_STORAGE_KEY);
    const cred = sessionStorage.getItem(SIGNUP_CREDENTIALS_KEY);
    if (!raw || !cred) {
      navigate("/etablissement/inscription/identifiants", { replace: true });
      return;
    }
    // Le mode claim ne passe jamais par cette étape (soumis directement depuis les identifiants).
    const mode = (JSON.parse(raw) as { mode?: string }).mode;
    if (mode === "claim") {
      navigate("/etablissement/inscription/identifiants", { replace: true });
      return;
    }
    setReady(true);
  }, [navigate]);

  const canProceed = !submitting;

  const onSubmit = async () => {
    if (!canProceed || !ready) return;
    const raw = localStorage.getItem(SIGNUP_STORAGE_KEY);
    const credRaw = sessionStorage.getItem(SIGNUP_CREDENTIALS_KEY);
    if (!raw || !credRaw) return;
    const base = JSON.parse(raw) as {
      nom?: string;
      numero_agrement?: string;
      type?: string;
      adresse?: string;
      telephone?: string;
      directeur_nom?: string;
      directeur_prenom?: string;
      email?: string;
    };
    const cred = JSON.parse(credRaw) as { password?: string; confirmPassword?: string };
    setError("");
    setSubmitting(true);
    const result = await signup({
      email: base.email ?? "",
      password: cred.password ?? "",
      confirmPassword: cred.confirmPassword ?? cred.password ?? "",
      type_utilisateur: base.type ?? "",
      profileData: {
        nom: base.nom ?? "",
        type: base.type ?? "",
        adresse: base.adresse ?? "",
        telephone: base.telephone,
        numero_agrement: base.numero_agrement,
        directeur_nom: base.directeur_nom,
        directeur_prenom: base.directeur_prenom,
      },
    });
    setSubmitting(false);
    if (result.ok) {
      sessionStorage.removeItem(SIGNUP_CREDENTIALS_KEY);
      localStorage.removeItem(SIGNUP_STORAGE_KEY);
      navigate("/etablissement/inscription/succes", { state: { mode: "creation" } });
    } else {
      setError(result.message || "Une erreur est survenue lors de l'inscription.");
    }
  };

  if (!ready) return null;

  return (
    <div className="min-h-screen grid grid-cols-1 md:grid-cols-2 relative">
      {/* Mobile background image */}
      <div className="absolute inset-0 md:hidden">
        <img src={'/maps-login-side.jpg'} alt="signup-documents" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-white/30" />
      </div>

      {/* Left: form */}
      <div className="relative flex items-center justify-center px-4 md:px-10 py-10">
        <div className="w-full max-w-2xl">
          <div className="flex justify-center"><Logo size={56} /></div>
          <div className="mt-6 text-sm" style={{ color: "#00553E" }}>
            <Link to="/etablissement/inscription/identifiants" className="inline-flex items-center gap-2">
              <ArrowLeft size={16} /> Retour
            </Link>
          </div>
          <h1 className="mt-2 text-2xl md:text-3xl font-semibold text-[#0B1F16]">
            Créer le compte de l'établissement
          </h1>
          <div className="mt-1 text-gray-500 font-semibold">Pièces justificatives</div>

          {error && (
            <div className="mt-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded-[10px] px-3 py-2">
              {error}
            </div>
          )}

          <div className="mt-6 text-sm rounded-[10px] px-4 py-3" style={{ color: "#0B1F16", backgroundColor: "#F3F8F6", border: "1px solid #D1E2DD" }}>
            <p className="text-gray-600">
              Une fois votre compte créé, connectez-vous puis rendez-vous dans « Vérification d'identité »
              depuis votre tableau de bord pour transmettre la carte CIP du responsable et l'arrêté
              ministériel — c'est ce qui déclenche la vérification de votre établissement.
            </p>
          </div>

          <div className="mt-6">
            <button
              type="button"
              className="w-full h-12 rounded-[10px] font-semibold cursor-pointer disabled:opacity-60"
              style={{ backgroundColor: canProceed ? "#00553E" : "#DCEFE9", color: canProceed ? "#FFFFFF" : "#7aa79a" }}
              disabled={!canProceed}
              onClick={onSubmit}
            >
              {submitting ? "Création du compte…" : "Créer le compte"}
            </button>
          </div>

          <div className="mt-8">
            <ProgressSteps step={4} total={5} />
          </div>
        </div>
      </div>

      {/* Right: image (desktop) */}
      <div className="relative hidden md:block">
        <img src={'/maps-login-side.jpg'} alt="signup-documents" className="absolute inset-0 w-full h-full object-cover" />
      </div>
    </div>
  );
}
